import React, { useState } from "react";
import api from './config/api';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Backdrop,
  CircularProgress,
  Paper,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Detectar idioma actual - PRIORIDAD: navegador > i18n > español
  const getCurrentLanguage = () => {
    // 1. Prioridad: idioma del navegador
    const browserLang = navigator.language || navigator.languages?.[0];
    if (browserLang) {
      // Extraer solo el código de idioma (ej: 'es-ES' -> 'es')
      return browserLang.split('-')[0];
    }
    
    // 2. Fallback: idioma actual de i18n
    if (i18n?.language) {
      return i18n.language;
    }
    
    // 3. Último fallback: español
    return 'es';
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validaciones del formulario
    if (!formData.name.trim()) {
      setError(t('register.validation.nameRequired'));
      return;
    }

    if (!formData.email.trim()) {
      setError(t('register.validation.emailRequired'));
      return;
    }

    if (!formData.password.trim()) {
      setError(t('register.validation.passwordRequired'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.validation.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('register.validation.passwordMinLength'));
      return;
    }

    if (!privacyAccepted) {
      setError(t('register.validation.privacyRequired'));
      return;
    }

    setIsLoading(true);

    try {
      // Datos para el registro con idioma detectado
      const registrationData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        language: getCurrentLanguage()
      };

      console.log('Registering with language:', registrationData.language);

      const response = await api.post('/users/register-public', registrationData);
      
      if (response.data.response.status_response === "OK") {
        // Registro exitoso, redirigir a login
        navigate('/login', { 
          state: { 
            message: t('register.success.message'),
            email: formData.email 
          }
        });
      } else {
        setError(response.data.errorMessage || t('register.error.generic'));
      }

    } catch (err) {
      console.error('Error en registro:', err);
      if (err.response?.data?.errorMessage) {
        setError(err.response.data.errorMessage);
      } else if (err.response?.data?.response?.message) {
        setError(err.response.data.response.message);
      } else {
        setError(t('register.error.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url("https://cdn.pixabay.com/photo/2018/03/10/09/45/businesswoman-3213659_1280.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box sx={{ p: 4, textAlign: 'center', background: '#f8f9fa' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#17293C', mb: 1 }}>
              {t('register.header.title')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('register.header.subtitle')}
            </Typography>
          </Box>

          {/* Form */}
          <Box sx={{ p: 4 }}>
            <Typography variant="h5" align="center" sx={{ mb: 4, fontWeight: 'bold' }}>
              {t('register.form.title')}
            </Typography>

            <form onSubmit={handleRegister}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('register.form.fields.name')}
                    variant="outlined"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    required
                    autoFocus
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('register.form.fields.email')}
                    type="email"
                    variant="outlined"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('register.form.fields.password')}
                    type="password"
                    variant="outlined"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    required
                    helperText={t('register.form.fields.passwordHelper')}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('register.form.fields.confirmPassword')}
                    type="password"
                    variant="outlined"
                    value={formData.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    required
                  />
                </Grid>
              </Grid>

              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}

              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>{t('register.freeAccount.title')}</strong>
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>{t('register.freeAccount.features.evaluations')}</li>
                  <li>{t('register.freeAccount.features.userProject')}</li>
                  <li>{t('register.freeAccount.features.basicAccess')}</li>
                  <li>{t('register.freeAccount.features.upgrade')}</li>
                </ul>
              </Alert>

              {/* Checkbox de Política de Privacidad */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('register.privacy.agreement')}{' '}
                    <Button
                      variant="text"
                      size="small"
                      href="https://storage.googleapis.com/amandahr/politica_privacidad_amanda.docx"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                    >
                      {t('register.privacy.privacyPolicy')}
                    </Button>
                  </Typography>
                }
                sx={{ 
                  mt: 2,
                  alignItems: 'center',
                  '& .MuiFormControlLabel-label': {
                    lineHeight: 1.2
                  }
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={goToLogin}
                  sx={{ px: 4 }}
                >
                  {t('register.form.buttons.hasAccount')}
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  sx={{ px: 4 }}
                >
                  {isLoading ? t('register.form.buttons.creating') : t('register.form.buttons.createAccount')}
                </Button>
              </Box>
            </form>
          </Box>
        </Paper>

        {/* Loading Backdrop */}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
          open={isLoading}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" />
            <Typography sx={{ mt: 2 }}>
              {t('register.loading.message')}
            </Typography>
          </Box>
        </Backdrop>
      </Container>
    </Box>
  );
};

export default Register;